/**
 * Leviathan Web - Reports Hub Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Central hub for all reports and statements
 */

import { Link } from 'react-router-dom';
import {
  FileText,
  TrendingUp,
  Truck,
  Calculator,
  Users,
  BarChart3,
  ChevronRight,
} from 'lucide-react';

interface ReportCard {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: 'steel' | 'amber' | 'success' | 'warning' | 'error';
}

const REPORTS: ReportCard[] = [
  {
    id: 'ar-aging',
    name: 'AR Aging',
    description: 'Accounts receivable aging by customer',
    icon: <BarChart3 className="w-8 h-8" />,
    path: '/reports/ar-aging',
    color: 'amber',
  },
  {
    id: 'sales',
    name: 'Sales Report',
    description: 'Revenue by product, customer, and time period',
    icon: <TrendingUp className="w-8 h-8" />,
    path: '/reports/sales',
    color: 'success',
  },
  {
    id: 'delivery',
    name: 'Delivery Report',
    description: 'Deliveries by driver, truck, and product',
    icon: <Truck className="w-8 h-8" />,
    path: '/reports/delivery',
    color: 'steel',
  },
  {
    id: 'tax',
    name: 'Tax Report',
    description: 'Tax liability by jurisdiction and type',
    icon: <Calculator className="w-8 h-8" />,
    path: '/reports/tax',
    color: 'warning',
  },
  {
    id: 'statements',
    name: 'Customer Statements',
    description: 'Generate and manage customer statements',
    icon: <FileText className="w-8 h-8" />,
    path: '/reports/statements',
    color: 'steel',
  },
];

/**
 * Report card component
 */
function ReportCardItem({ report }: { report: ReportCard }) {
  const colorMap = {
    steel: { border: 'border-steel', text: 'text-steel', glow: 'rgba(92,140,199,0.6)' },
    amber: { border: 'border-amber', text: 'text-amber', glow: 'rgba(255,149,0,0.6)' },
    success: {
      border: 'border-success-green',
      text: 'text-success-green',
      glow: 'rgba(34,197,94,0.6)',
    },
    warning: {
      border: 'border-warning-amber',
      text: 'text-warning-amber',
      glow: 'rgba(245,158,11,0.6)',
    },
    error: { border: 'border-error-red', text: 'text-error-red', glow: 'rgba(239,68,68,0.6)' },
  };

  const colors = colorMap[report.color];

  return (
    <Link to={report.path} className="group relative">
      {/* Hover corner accents */}
      <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber opacity-0 group-hover:opacity-100 transition-opacity" />

      <div
        className={`bg-panel-dark border-l-4 ${colors.border} border border-panel-border p-6 hover:bg-amber/5 transition-all h-full`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div
              className={`${colors.text}`}
              style={{ filter: `drop-shadow(0 0 8px ${colors.glow})` }}
            >
              {report.icon}
            </div>
            <div>
              <h3 className="text-lg font-mono text-text-primary uppercase tracking-wide group-hover:text-amber transition-colors">
                {report.name}
              </h3>
              <p className="text-text-secondary font-mono text-sm mt-1">
                {report.description}
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-text-tertiary group-hover:text-amber transition-colors" />
        </div>
      </div>
    </Link>
  );
}

/**
 * Quick report button
 */
function QuickReportButton({ label, path }: { label: string; path: string }) {
  return (
    <Link
      to={path}
      className="bg-background border border-panel-border hover:border-amber/50 hover:bg-amber/5 p-3 text-center transition-colors"
    >
      <span className="text-text-primary font-mono text-sm hover:text-amber">
        {label}
      </span>
    </Link>
  );
}

/**
 * Reports Hub Page
 */
const ReportsPage = () => {
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary font-mono uppercase tracking-wider">
            Reports
          </h1>
          <p className="text-sm text-text-secondary font-mono mt-1">
            Generate and view business reports
          </p>
        </div>
      </div>

      {/* Report Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {REPORTS.map((report) => (
          <ReportCardItem key={report.id} report={report} />
        ))}
      </div>

      {/* Quick Reports Section */}
      <div className="relative">
        <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6">
          <h3 className="text-lg font-mono text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
            Quick Reports
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <QuickReportButton label="Today's Sales" path="/reports/sales?period=today" />
            <QuickReportButton
              label="This Week's Deliveries"
              path="/reports/delivery?period=week"
            />
            <QuickReportButton label="Overdue AR" path="/reports/ar-aging" />
            <QuickReportButton
              label="Monthly Tax Summary"
              path="/reports/tax?period=month"
            />
          </div>
        </div>
      </div>

      {/* Info Panel */}
      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="flex items-start space-x-3">
          <Users className="w-5 h-5 text-amber flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-text-secondary font-mono text-sm">
              Reports can be generated for any date range and exported to PDF or Excel. Customer
              statements can be batch generated and emailed directly to customers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
